import { FIELD_ERROR_TEXTS } from '../../shared/texts'

// The message for one invalid field, from the browser's own verdict (ValidityState) and the
// field's attributes. Pure — no DOM — so it can be tested in node. The texts live in constants.ts.

export interface FieldValidity {
    valueMissing?: boolean
    typeMismatch?: boolean
    badInput?: boolean
    rangeUnderflow?: boolean
    rangeOverflow?: boolean
    stepMismatch?: boolean
    tooLong?: boolean
    tooShort?: boolean
    patternMismatch?: boolean
}

export interface FieldInfo {
    label: string
    type: string // input type, 'select', 'textarea'
    min?: string
    max?: string
    step?: string
    maxLength?: number
    minLength?: number
}

type Texts = { readonly [K in keyof typeof FIELD_ERROR_TEXTS]: string }

// 0.01 → „0,01" — numbers in the message read as German.
const num = (v: string | undefined) => (v ?? '').replace('.', ',')

function fill(template: string, f: FieldInfo, texts: Texts): string {
    const label = f.label.trim()
    // No name: „dieses Feld" without quotes, and the sentence starts with a capital.
    const named = label ? template : template.split('„{label}“').join(texts.unnamed)
    const out = named
        .split('{label}').join(label || texts.unnamed)
        .split('{min}').join(num(f.min))
        .split('{max}').join(num(f.max))
        .split('{step}').join(num(f.step))
        .split('{maxLength}').join(String(f.maxLength ?? ''))
        .split('{minLength}').join(String(f.minLength ?? ''))
    return out.charAt(0).toUpperCase() + out.slice(1)
}

// null when the field is valid.
export function fieldMessage(v: FieldValidity, f: FieldInfo, texts: Texts = FIELD_ERROR_TEXTS): string | null {
    const t = (key: keyof Texts) => fill(texts[key], f, texts)
    // Something typed that is not a number / date at all: the browser reports the value as empty
    // AND badInput — „angeben" would be wrong, the user did type something.
    if (v.badInput) return f.type === 'number' ? t('number') : f.type === 'date' ? t('date') : t('invalid')
    if (v.valueMissing) return t('required')
    if (v.typeMismatch) return f.type === 'email' ? t('email') : f.type === 'url' ? t('url') : t('invalid')
    if (v.rangeUnderflow) return t('min')
    if (v.rangeOverflow) return t('max')
    if (v.stepMismatch) return t('step')
    if (v.tooLong) return t('tooLong')
    if (v.tooShort) return t('tooShort')
    if (v.patternMismatch) return t('invalid')
    return null
}
